import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Tooltip from '../Tooltip'
import { Box, Typography } from '@material-ui/core'
import clsx from 'clsx'

const axisGray = '#C8C8C8'
const tickGray = '#707070'

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: 15,
    marginBottom: 12,
  },
  barChart: {
    marginLeft: theme.spacing(1),
  },
  legend: {
    display: 'flex',
    marginBottom: 20,
  },
  legendIcon: {
    height: '8px',
    width: '12px',
    border: `1px solid ${theme.extras.variables.colors.textSubtle}`,
    marginRight: 6,
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  barContainer: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  bar: {
    height: '100%',
    boxSizing: 'border-box',
    transition: theme.transitions.create(['width'], {
      duration: 300,
    }),
  },
  axis: {
    position: 'relative',
    height: '20px',
    marginTop: '3px',
    display: 'flex',
    justifyContent: 'space-between',
    borderTop: `1px solid ${axisGray}`,
  },
  tick: {
    position: 'absolute',
    width: '1px',
    backgroundColor: axisGray,
    transform: 'translateX(-50%)',
  },
  labeledTick: {
    height: '8px',
  },
  unlabeledTick: {
    height: '5px',
  },
  tickLabel: {
    position: 'absolute',
    color: tickGray,
    transform: 'translateX(-50%)',
    marginTop: '10px',
  },
}))

// creates horizontal stacked bar chart
const StackedHorizontalBar = ({
  values,
  highlightedIdx,
  colors,
  highlightColor,
  barHeight,
  ticks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
  labeledTicks = [0, 50, 100],
  legendText = '',
  getTooltipText = ({ barIdx, value }) => null,
}) => {
  // Calculate the total sum of the values
  const total = values.reduce(
    (sum, value) => sum + value,
    0,
  )
  const classes = useStyles()

  return (
    <Box className={classes.root}>
      {/* Legend */}
      <Box className={classes.legend}>
        <Box className={classes.legendIcon} />
        <Typography variant="body2">
          {legendText}
        </Typography>
      </Box>
      {/* Bars */}
      <Box className={classes.barChart}>
        <Box
          className={classes.barContainer}
          style={{
            height: `${barHeight}px`,
          }}
        >
          {values.map((value, barIdx) => {
            const widthPercentage = (value / total) * 100
            return (
              <Tooltip
                key={barIdx}
                title={getTooltipText({ barIdx, value })}
                unwrappedChildren
              >
                <Box
                  className={classes.bar}
                  style={{
                    width: `${widthPercentage}%`,
                    height:
                      barIdx === highlightedIdx
                        ? `${barHeight + 2}px`
                        : '100%',
                    margin:
                      barIdx === highlightedIdx
                        ? '-1px 0'
                        : '0', // Center the border if highlighted
                    backgroundColor:
                      colors[barIdx % colors.length],
                    border:
                      barIdx === highlightedIdx
                        ? `1px solid ${highlightColor}`
                        : 'none',
                  }}
                />
              </Tooltip>
            )
          })}
        </Box>
        {/* Axis */}
        <Box className={classes.axis}>
          {/* Ticks with labels */}
          {ticks.map((tick, idx) => (
            <Box
              key={idx}
              className={clsx(classes.tick, {
                [classes.labeledTick]: labeledTicks.includes(
                  tick,
                ),
                [classes.unlabeledTick]: !labeledTicks.includes(
                  tick,
                ),
              })}
              style={{ left: `${tick}%` }}
            >
              {labeledTicks.includes(tick) && (
                <Typography
                  variant="caption"
                  className={classes.tickLabel}
                >
                  {tick}%
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  )
}

export default StackedHorizontalBar
